import { useMutation, useQuery } from "@apollo/client";
import FileDownload from "@mui/icons-material/FileDownload";
import { Alert, Box, Button, Paper, Typography } from "@mui/material";
import { LineChart } from "@mui/x-charts/LineChart";
import { useAlerts } from "components/Alerts/AlertProvider";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { GET_STAFF_CERS_ANALYTICS } from "hooks/useStaffCersAnalytics";
import { useCallback, useMemo, useState } from "react";
import { downloadFromUrl } from "util/download";

import { RateRow, RateTable } from "./RateTable";
import { TenantAutocomplete, TenantOption } from "./TenantAutocomplete";

function rejectionsHref(filter: string): string {
  return `/staff/cers/rejections?omnisearch=${encodeURIComponent(filter)}`;
}

function windowLabel(startIso: string, endIso: string): string {
  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
      timeZone: "UTC",
    });
  return `${fmt(startIso)} – ${fmt(endIso)}`;
}

const EXPORT_STAFF_CERS_ANALYTICS = gql(/* GraphQL */ `
  mutation ExportStaffCersAnalytics($tenantId: ID) {
    exportStaffCersAnalytics(tenantId: $tenantId) {
      success
      downloadUrl
      errorMsg
    }
  }
`);

function ExportButton({ tenantId }: { tenantId?: string }) {
  const alerts = useAlerts();
  // Alerts + download fire from onCompleted rather than effects keyed on the
  // mutation's data: Apollo doesn't clear `data` between calls, so a repeat
  // outcome (second success, same errorMsg) never re-triggers an effect.
  const [exportMutation, { loading }] = useMutation(EXPORT_STAFF_CERS_ANALYTICS, {
    onCompleted: (data) => {
      const result = data.exportStaffCersAnalytics;
      if (result?.success) {
        alerts.success("Export ready");
      } else if (result?.errorMsg) {
        alerts.error(`Export failed: ${result.errorMsg}`);
      }
      if (result?.downloadUrl) {
        downloadFromUrl(result.downloadUrl);
      }
    },
    onError: (err) => alerts.error(`Export failed: ${err.message}`),
  });

  const handleClick = useCallback(() => {
    exportMutation({ variables: { tenantId } });
  }, [exportMutation, tenantId]);

  return (
    <Button
      size="medium"
      loading={loading}
      startIcon={<FileDownload />}
      onClick={handleClick}
      aria-label="Export"
    >
      Export
    </Button>
  );
}

export const Analytics = () => {
  useBreadcrumb({ label: "Analytics", to: "/staff/cers/analytics" });

  const [tenant, setTenant] = useState<TenantOption | null>(null);
  const { data, loading, error } = useQuery(GET_STAFF_CERS_ANALYTICS, {
    variables: { tenantId: tenant?.id },
    fetchPolicy: "cache-and-network",
  });
  const analytics = data?.staffCersAnalytics;

  const tenantRows: RateRow[] = useMemo(
    () =>
      (analytics?.byTenant ?? []).map((t) => ({
        key: t.tenantId ?? "unmatched",
        name: t.tenantName,
        decided: t.decided,
        substantiveRejections: t.substantiveRejections,
        ratePct: t.ratePct ?? null,
        facilities: t.facilities,
      })),
    [analytics?.byTenant]
  );

  const cupaRows: RateRow[] = useMemo(
    () =>
      (analytics?.byCupa ?? []).map((c) => ({
        // cupaCode is COALESCE(d."cupaCode", '') in the resolver, so empty string signals the
        // no-CUPA bucket; fall back to displayName for the React key to keep it unique.
        key: c.cupaCode || c.displayName,
        name: c.displayName,
        decided: c.decided,
        substantiveRejections: c.substantiveRejections,
        ratePct: c.ratePct ?? null,
        cupaCode: c.cupaCode,
      })),
    [analytics?.byCupa]
  );

  const moduleRows: RateRow[] = useMemo(
    () =>
      (analytics?.byModule ?? []).map((m) => ({
        key: m.module,
        name: prettyPrintEnumValue(m.module),
        decided: m.decided,
        substantiveRejections: m.substantiveRejections,
        ratePct: m.ratePct ?? null,
      })),
    [analytics?.byModule]
  );

  const categoryRows: RateRow[] = useMemo(
    () =>
      (analytics?.byIssueCategory ?? []).map((c) => ({
        key: c.category,
        name: prettyPrintEnumValue(c.category),
        decided: c.count,
        // The category variant renders "Count" and "% of Substantive" only; substantiveRejections is not displayed.
        substantiveRejections: 0,
        ratePct: c.pctOfSubstantive,
      })),
    [analytics?.byIssueCategory]
  );

  const { trendData, trendMonths, trendRates } = useMemo(() => {
    const data = analytics?.trend ?? [];
    return {
      trendData: data,
      trendMonths: data.map((m) => m.month),
      trendRates: data.map((m) => m.ratePct ?? null),
    };
  }, [analytics?.trend]);

  const showTenantEmptyState =
    tenant != null &&
    !loading &&
    (analytics?.byCupa ?? []).length === 0 &&
    (analytics?.byModule ?? []).length === 0;

  if (error) {
    return (
      <Box sx={{ pt: 3, px: 3 }}>
        <Alert severity="error">Failed to load CERS analytics: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, px: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6">CERS Rejection Analytics</Typography>
          <Typography variant="caption" color="text.secondary">
            {analytics
              ? `Trailing 12 months: ${windowLabel(analytics.windowStart, analytics.windowEnd)} · substantive rejections ÷ decided modules`
              : "Trailing 12 months · substantive rejections ÷ decided modules"}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
          <TenantAutocomplete value={tenant} onChange={setTenant} />
          <ExportButton tenantId={tenant?.id} />
        </Box>
      </Box>

      {showTenantEmptyState ? (
        <Alert severity="info">
          No CERS decisions in the last 12 months for this organization.
        </Alert>
      ) : null}

      <Paper elevation={0} sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Monthly Rejection Rate
        </Typography>
        <LineChart
          height={280}
          xAxis={[{ scaleType: "point", data: trendMonths }]}
          series={[
            {
              data: trendRates,
              label: "Rejection rate %",
              connectNulls: false,
              valueFormatter: (value, { dataIndex }) => {
                const month = trendData[dataIndex];
                if (value == null || !month) return "—";
                return `${value}% (${month.substantiveRejections}/${month.decided})`;
              },
            },
          ]}
          loading={loading && !analytics}
        />
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        {tenant == null ? (
          <RateTable
            title="Rate by Organization"
            nameHeader="Organization"
            rows={tenantRows}
            loading={loading && !analytics}
            showFacilities
            getHref={(row) =>
              row.key === "unmatched"
                ? null
                : rejectionsHref(
                    `tenantId:"${row.key}" decidedLast12mo:true commentType:substantive_rejection`
                  )
            }
          />
        ) : null}
        <RateTable
          title="Rate by CUPA"
          nameHeader="CUPA"
          rows={cupaRows}
          loading={loading && !analytics}
          getHref={(row) =>
            row.cupaCode
              ? rejectionsHref(
                  `cupaCode:"${row.cupaCode}" decidedLast12mo:true commentType:substantive_rejection`
                )
              : null
          }
        />
        <RateTable
          title="Rate by Module"
          nameHeader="Module"
          rows={moduleRows}
          loading={loading && !analytics}
        />
        <RateTable
          title="Top Issue Categories"
          nameHeader="Issue Category"
          rows={categoryRows}
          loading={loading && !analytics}
          variant="category"
        />
      </Box>
    </Box>
  );
};
